import { BackgroundCheckError, getSsn } from '@/backend/features/background-check';
import { BackgroundCheckConfig, BackgroundCheckQuota } from '@/backend/features/background-check/defaults';
import {
    checkAddress,
    checkAddressLine1,
    checkCity,
    checkDateOfBirth,
    checkFirstName,
    checkGender,
    checkLastName,
    checkPostalCode,
    checkSsn,
    checkState,
    checkStateOfWork,
    commonBackgroundCheckRequestValidation,
} from '@/backend/features/background-check/platform-functions/utils/validateBackgroundCheckRequestParams';
import { BackgroundCheckColumns } from '@/backend/features/background-check/shared';
import { IntellicorpBackgroundCheckType, intellicorpBackgroundCheckTypes } from '@/backend/features/background-check/types';
import { ExtendedAccountData } from '@/backend/features/contact-management/entities/extended-account/types';
import { AppContext } from '@juvare/platform-app/App';
import { BackgroundCheckPerson, BackgroundCheckType as JasBackgroundCheckType } from '@juvare/platform-app/Integration';
import { AppState } from '@juvare/platform-sdk-react/types';

interface ValidateIntellicorpRequestParams {
    context: AppContext<AppState>;
    accountData: ExtendedAccountData;
    backgroundCheck: BackgroundCheckColumns;
    backgroundCheckType: IntellicorpBackgroundCheckType;
    config: BackgroundCheckConfig;
    quota: BackgroundCheckQuota;
}

export interface ValidateIntellicorpRequestResponse {
    jasType: JasBackgroundCheckType;
    person: BackgroundCheckPerson;
    isUsingQuota?: boolean;
}

export function validateIntellicorpRequest({
    context,
    accountData,
    backgroundCheck,
    backgroundCheckType,
    config,
    quota,
}: ValidateIntellicorpRequestParams): ValidateIntellicorpRequestResponse {
    const configByType = config.type[backgroundCheckType];
    const jasType = intellicorpBackgroundCheckTypes[backgroundCheckType].jasType;

    if (!jasType) {
        throw new BackgroundCheckError(
            `Background check is not enabled for ${config.type[backgroundCheckType].label} type`,
            'processingError',
        );
    }

    commonBackgroundCheckRequestValidation({
        context,
        backgroundCheck,
        backgroundCheckType,
        config,
        quota,
    });

    if (configByType.requiresFormValidation) {
        // Handle the validation separate by type
        switch (backgroundCheckType) {
            case 'cori':
                {
                    checkFirstName(accountData);
                    checkLastName(accountData);
                    checkDateOfBirth(accountData);
                    checkAddress(accountData);
                    checkAddressLine1(accountData);
                    checkCity(accountData);
                    checkPostalCode(accountData);
                    checkGender(accountData);
                }
                break;
            case 'vsos':
                {
                    checkFirstName(accountData);
                    checkLastName(accountData);
                    checkDateOfBirth(accountData);
                    checkStateOfWork(accountData);
                }
                break;
            default: {
                checkFirstName(accountData);
                checkLastName(accountData);
                checkDateOfBirth(accountData);
                checkAddress(accountData);
                checkAddressLine1(accountData);
                checkCity(accountData);
                checkState(accountData);
                checkPostalCode(accountData);
                checkSsn(accountData);
            }
        }
    }

    const splitAddressLine =
        backgroundCheckType !== 'vsos' && accountData.address?.addressLine1
            ? splitAddressLine1(accountData.address.addressLine1)
            : undefined;

    const ssn = getSsn(context, accountData);

    return {
        jasType,
        person: {
            firstName: accountData.firstName,
            middleInitial: accountData.middleName?.substring(0, 1).toUpperCase(),
            lastName: accountData.lastName,
            email: accountData.email,
            dob: accountData.dateOfBirth || '',
            ssn,
            houseNumber: splitAddressLine?.houseNumber || '',
            streetName: splitAddressLine?.streetName || '',
            city: accountData.address?.city || '',
            state: accountData.address?.state || '',
            stateOfWork: (accountData.stateOfWork ?? accountData.address?.state) || '',
            zip: accountData.address?.postalCode || '',
        },
        isUsingQuota: quota[backgroundCheckType].enabled,
    };
}

function splitAddressLine1(address: string): { houseNumber: string; streetName: string } {
    // Could not find a better way to do this
    const trimmedAddress = address.trim();
    const i = trimmedAddress.indexOf(' ');
    if (i !== -1) {
        return {
            houseNumber: trimmedAddress.substring(0, i).trim(),
            streetName: trimmedAddress.substring(i + 1).trim(),
        };
    } else {
        throw new BackgroundCheckError('Address Line 1 should contain house number and street name separated by space', 'missingFields');
    }
}
